import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {NotificationOffLine} from '@dropbox/dig-icons/assets';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {Facepile} from 'components/DSYS/Facepile';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {useAtomValue} from 'jotai';
import {useMemo} from 'react';
import {
  formatOffsetRelative,
  formatZoneTime,
  getViewerTimeZone,
  isOutsideWorkingHours,
  offsetFromViewer,
  zoneBucketKey,
  zoneFriendlyName,
} from 'views/home/dashboard/coreHours';
import {logDashboardInteraction} from 'views/home/dashboard/dashboardAnalytics';
import {DashboardState, TimezoneGroup} from 'views/home/dashboard/dashboardModel';
import {Tile} from 'views/home/dashboard/Tile';
import {TileListSkeleton} from 'views/home/dashboard/tiles/TileListSkeleton';
import {TileScroller} from 'views/home/dashboard/TileScroller';
import {useTileSpan} from 'views/home/dashboard/TileSizeContext';

import styles from './TeamTimezonesTile.module.css';

const MAX_FACES = 4;

const InitialsPile = ({initials, total}: {initials: string[]; total: number}) => {
  const shown = initials.slice(0, MAX_FACES);
  const overflow = total - shown.length;
  return (
    <Box display="flex" className={styles.tzFacepile} tagName="span">
      {overflow > 0 && (
        <Box className={styles.tzInitial} tagName="span">
          +{overflow}
        </Box>
      )}
      {[...shown].reverse().map((initial, idx) => (
        <Box key={`${initial}-${idx}`} className={styles.tzInitial} tagName="span">
          {initial}
        </Box>
      ))}
    </Box>
  );
};

/**
 * Shared row: label (+ offset sub-label when not compact) on the left, facepile +
 * local time on the right. In compact mode the offset is dropped so the row sits
 * on a single line, which keeps short (small/wide) tiles from clipping.
 */
const ZoneRow = ({
  label,
  subLabel,
  ldaps,
  initials,
  memberCount,
  timeLabel,
  offHours = false,
  compact = false,
  onProfileLinkClick,
}: {
  label: string;
  subLabel: string;
  ldaps: string[];
  initials?: string[];
  memberCount: number;
  timeLabel: string;
  offHours?: boolean;
  compact?: boolean;
  onProfileLinkClick?: (ldap: string) => void;
}) => (
  <Split alignY="center" gap="8">
    <Split.Item width="fill">
      {compact ? (
        <Text
          isBold
          color={!offHours ? 'standard' : 'subtle'}
          className={atoms({display: 'flex', alignItems: 'center'})}
        >
          {label}
          {offHours && <UIIcon src={NotificationOffLine} />}
        </Text>
      ) : (
        <Stack gap="2">
          <Text
            isBold
            color={!offHours ? 'standard' : 'subtle'}
            className={atoms({display: 'flex', alignItems: 'center'})}
            style={{gap: 2}}
          >
            {label}
            {offHours && <UIIcon size="small" src={NotificationOffLine} />}
          </Text>
          <Box>
            <Text size="small" color="faint">
              {subLabel}
            </Text>
          </Box>
        </Stack>
      )}
    </Split.Item>
    <Split.Item>
      <Box display="flex" alignItems="center" style={{gap: 10}}>
        {ldaps.length > 0 ? (
          <Facepile
            ldaps={ldaps}
            size="xsmall"
            showSelf
            overflow={MAX_FACES}
            linkToProfile
            onProfileLinkClick={onProfileLinkClick}
          />
        ) : (
          <InitialsPile initials={initials ?? []} total={memberCount} />
        )}
        <Text isBold color="standard" style={{fontVariantNumeric: 'tabular-nums'}}>
          {timeLabel}
        </Text>
      </Box>
    </Split.Item>
  </Split>
);

type SortableZoneRow = {
  row: React.ComponentProps<typeof ZoneRow> & {key: string};
  sortOffset: number;
};

export const TeamTimezonesTile = ({state}: {state: DashboardState}) => {
  const span = useTileSpan();
  // Only 1 or 2 rows are supported (see TILE_CATALOG.teamTimezones). A 1-row tile
  // is short, so its rows drop the offset sub-label to fit one line.
  const compact = span.rows === 1;
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const viewerTz = useMemo(
    () => state.viewerZone?.timeZone ?? employee?.time_zone ?? getViewerTimeZone(),
    [state.viewerZone?.timeZone, employee?.time_zone]
  );
  const selfLdap = employee?.ldap;
  const bodyRef = useSkeletonFadeIn(state.timezonesLoading);

  // Split the user's own zone out so the viewer can be merged into that bucket,
  // then place the merged row back into the chronological west-to-east order.
  const {viewerGroup, otherGroups} = useMemo(() => {
    const sorted = [...state.timezoneGroups].sort(
      (a, b) =>
        offsetFromViewer(state.now, a.timeZone, viewerTz) -
        offsetFromViewer(state.now, b.timeZone, viewerTz)
    );
    // Match on the row key, not just the offset, so an Irish viewer never
    // absorbs UK teammates (or vice versa).
    const viewerKey = zoneBucketKey(state.now, viewerTz);
    const own = sorted.find((g) => zoneBucketKey(state.now, g.timeZone) === viewerKey);
    return {
      viewerGroup: own,
      otherGroups: sorted.filter((g): g is TimezoneGroup => g !== own),
    };
  }, [state.timezoneGroups, state.now, viewerTz]);

  // Live mode hides mock rows; show a skeleton until the zone source has loaded.
  if (state.timezonesLoading) {
    return <TileListSkeleton tileId="teamTimezones" accessory="none" trailing="chip" rows={3} />;
  }

  // The viewer's facepile shows themselves plus any teammates in their zone.
  const viewerLdaps = [selfLdap, ...(viewerGroup?.ldaps ?? [])].filter((ldap): ldap is string =>
    Boolean(ldap)
  );
  const viewerCount = viewerLdaps.length;

  // One ordered list. The list scrolls inside the tile, so every
  // zone is shown regardless of size rather than being capped to fit.
  const rows = (
    [
      {
        row: {
          key: 'viewer',
          label: zoneFriendlyName(state.now, viewerTz),
          subLabel: formatOffsetRelative(0),
          ldaps: viewerLdaps,
          memberCount: viewerCount,
          timeLabel: formatZoneTime(state.now, viewerTz),
          offHours: isOutsideWorkingHours(state.now, viewerTz),
        },
        sortOffset: 0,
      },
      ...otherGroups.map((group) => ({
        row: {
          key: group.id,
          label: group.label,
          subLabel: formatOffsetRelative(offsetFromViewer(state.now, group.timeZone, viewerTz)),
          ldaps: group.ldaps,
          initials: group.initials,
          memberCount: group.memberCount,
          timeLabel: formatZoneTime(state.now, group.timeZone),
          offHours: isOutsideWorkingHours(state.now, group.timeZone),
        },
        sortOffset: offsetFromViewer(state.now, group.timeZone, viewerTz),
      })),
    ] satisfies SortableZoneRow[]
  )
    .sort((a, b) => a.sortOffset - b.sortOffset || a.row.label.localeCompare(b.row.label))
    .map(({row}) => row);

  const handleProfileLinkClick = (ldap: string) =>
    logDashboardInteraction({
      action: 'facepile_click',
      tileId: 'teamTimezones',
      target: ldap,
      destination: 'profile',
    });

  return (
    <Tile tileId="teamTimezones" bodyRef={bodyRef}>
      <TileScroller gap="4">
        {rows.map(({key, ...row}) => (
          <Box
            key={key}
            {...withShade({style: {marginInline: -8}})}
            paddingX="8"
            paddingY="6"
            borderRadius="Medium"
          >
            <ZoneRow {...row} compact={compact} onProfileLinkClick={handleProfileLinkClick} />
          </Box>
        ))}
      </TileScroller>
    </Tile>
  );
};
